import type { Sfx } from './audio';
import { SLIDE_ANGLE, bodyLeft, isVessel } from './boats';
import type { LevelDef } from './levels';
import type { Phase, ViewState, VisualEvent } from './sim';
import { ACTIONS } from './weapons';

/** A vessel with less body than this left is burning: the crackle plays until it sinks or is mended. */
const BURNING = 0.25;
/**
 * A bounce only sounds when the thing arrived at this speed (px per tick) or faster. The sim
 * counts a bounce every tick a resting grenade settles into the ground, which is no sound at all.
 */
const BOUNCE_MIN_SPEED = 1.2;

/**
 * What a match sounds like: visual events become one-shots, and things the events do not
 * announce (a throw leaving a hand, a bounce, a mine's countdown, coins moving, a boat going
 * under) are noticed by watching the view from frame to frame. Works the same on the host and
 * on replicas.
 */
export class MatchSounds {
  /** Per projectile id: its bounce count and speed last frame (a bounce is heard by the speed it arrived at). */
  private known = new Map<number, { bounces: number; speed: number; weapon: string; frag: boolean }>();
  private mineFuse = new Map<number, number>();
  /** Per boat id: what it sounded like last frame, so flips (going under, tilting, catching fire) play once. */
  private boats = new Map<string, { sunk: boolean; sliding: boolean; burning: boolean }>();
  private coins: number | null = null;
  private digs = 0;
  /** Guns that have reported this frame: a burst's rounds all land in one frame and are one sound. */
  private shots = new Set<string>();
  /** A vessel blew up this frame: its splash waits for the fireball instead of playing at once. */
  private boomed = false;
  private lastTurnUnit: string | null = null;
  private phase: Phase | null = null;

  constructor(private readonly sfx: Sfx, private readonly localId: string) {}

  start(level: LevelDef): void {
    this.sfx.ambience(level.waterY !== null ? 'sea' : 'wind');
  }

  stop(): void {
    this.sfx.stopAll();
  }

  /** Call once per frame with the events the renderer is about to get. */
  frame(view: ViewState, visual: readonly VisualEvent[]): void {
    this.digs = 0;
    this.shots.clear();
    this.boomed = false;
    for (const ev of visual) this.event(ev);
    if (this.digs > 0) this.sfx.dig(Math.min(5, this.digs));
    this.projectiles(view);
    this.mines(view);
    this.vessels(view);
    this.turns(view);
    this.purse(view);
  }

  private event(ev: VisualEvent): void {
    const s = this.sfx;
    switch (ev.kind) {
      case 'explosion':
        s.boom(ev.r);
        break;
      case 'splash':
        s.splash();
        break;
      case 'shot': {
        // One report per gun per frame: the SMG's five rounds arrive together and rattle once.
        const weapon = ev.weapon ?? 'gun';
        if (this.shots.has(weapon)) break;
        this.shots.add(weapon);
        s.shot(weapon);
        break;
      }
      case 'kick':
        s.swing(ev.weapon ?? 'boot');
        break;
      case 'damage':
        s.hit(ev.amount);
        break;
      case 'panel':
        s.crack();
        break;
      case 'hook':
        s.clink();
        break;
      case 'build':
        s.thud();
        break;
      case 'heal':
        s.heal();
        break;
      case 'dig':
        this.digs++;
        break;
      case 'teleport':
        s.zap();
        break;
      case 'bolt':
        s.thunk();
        break;
      case 'stick':
        s.splat();
        break;
      case 'boom':
        // The water closing over the wreck is the sink's job (see `vessels`), a moment after this.
        this.boomed = true;
        s.bigBoom(ev.w);
        break;
      case 'catapult':
        // The boulder and this event land in the same frame, so the arm's creak and release cover the launch.
        s.catapult();
        break;
      case 'wave':
        s.horn();
        break;
      case 'turn':
        // A chime when a turn of yours begins, not when you switch moles within it.
        if (ev.playerId === this.localId && ev.unitId !== this.lastTurnUnit) s.turn();
        this.lastTurnUnit = ev.unitId;
        break;
      case 'callout':
        if (ev.text === 'Headshot!') s.sting('headshot');
        else if (ev.text === 'Direct hit!') s.sting('direct');
        else if (ev.text === 'Long shot!') s.sting('long');
        else if (ev.text === 'Second wind!') s.heal();
        else if (ev.text.startsWith('Coin flip')) s.coin(true);
        else if (/takes round \d/.test(ev.text)) s.fanfare('round');
        else if (ev.text.startsWith('Round') && ev.text.endsWith('a draw')) s.fanfare('draw');
        break;
      case 'reset':
        // The field is rebuilt: forget everything, and put out the fires before the boats come back new.
        this.known.clear();
        this.mineFuse.clear();
        for (const id of this.boats.keys()) this.sfx.stopLoop(`fire:${id}`);
        this.boats.clear();
        break;
      default:
        break;
    }
  }

  /**
   * New projectiles swish (or twang, or groan, or drone across the sky), bounces tink or boing,
   * RPGs roar and TNT fizzes until they are gone.
   */
  private projectiles(view: ViewState): void {
    const seen = new Set<number>();
    let plane = false;
    for (const p of view.projectiles) {
      if (!p.alive) continue;
      seen.add(p.id);
      const k = this.known.get(p.id);
      const speed = Math.hypot(p.vx, p.vy);
      if (!k) {
        this.known.set(p.id, { bounces: p.bounces, speed, weapon: p.weapon, frag: !!p.frag });
        if (p.frag) continue;
        const def = ACTIONS[p.weapon];
        const power = def.speed > 0 ? Math.min(1, speed / def.speed) : 0.6;
        switch (p.weapon) {
          case 'rpg':
            this.sfx.whoosh(1);
            this.sfx.startRocket(`p${p.id}`, p.homing);
            break;
          case 'mortar':
            this.sfx.mortar();
            break;
          case 'crossbow':
            this.sfx.twang();
            break;
          case 'airstrike':
            // All five bombs appear in one frame: the plane flies over once, the way the bombs drift.
            if (!plane) {
              plane = true;
              this.sfx.plane(p.vx < 0 ? -1 : 1);
            }
            break;
          case 'catapult':
            // The 'catapult' event plays the arm and the release; the boulder is quiet until it lands.
            break;
          case 'massive':
            this.sfx.heave();
            break;
          case 'tnt':
            // Set down, not thrown: only the fuse, until it blows.
            this.sfx.startFuse(`p${p.id}`);
            break;
          case 'sticky':
            this.sfx.wetWhoosh(power);
            break;
          default:
            this.sfx.whoosh(power, !!def.gear);
        }
        continue;
      }
      const arrived = k.speed;
      k.speed = speed;
      if (p.bounces > k.bounces) {
        k.bounces = p.bounces;
        // A sticky bomb's bite counts as a bounce, but the 'stick' event has already splatted for it.
        if (p.stuck || arrived < BOUNCE_MIN_SPEED) continue;
        if (p.weapon === 'ball') this.sfx.bounce('ball');
        else if (p.weapon === 'rubber') this.sfx.bounce('boing');
        // Heavy things land with a thud: the massive bomb, and a TNT charge dropping onto the ground.
        else if (p.weapon === 'massive' || p.weapon === 'tnt') this.sfx.thud();
        else this.sfx.bounce('tink', !!p.frag);
      }
    }
    for (const id of [...this.known.keys()]) {
      if (seen.has(id)) continue;
      this.known.delete(id);
      this.sfx.stopLoop(`p${id}`);
    }
  }

  /** A mine that has been set off beeps down to the bang. */
  private mines(view: ViewState): void {
    const seen = new Set<number>();
    for (const m of view.mines) {
      seen.add(m.id);
      const was = this.mineFuse.get(m.id) ?? -1;
      if (m.fuse >= 0 && (was < 0 || Math.floor(was / 10) !== Math.floor(m.fuse / 10))) this.sfx.beep();
      this.mineFuse.set(m.id, m.fuse);
    }
    for (const id of [...this.mineFuse.keys()]) if (!seen.has(id)) this.mineFuse.delete(id);
  }

  /**
   * Boats: a vessel that is nearly done burns (a crackle until it sinks or is mended), any boat
   * going under splashes and gurgles, and a raft creaks when it tilts far enough to slide moles.
   * The first sight of a boat (the start, a new fight, joining late) is taken as given: a boat
   * that is already sunk or tilted does not splash or creak, but one already burning crackles.
   */
  private vessels(view: ViewState): void {
    const seen = new Set<string>();
    for (const b of view.boats) {
      seen.add(b.id);
      const burning = isVessel(b) && !b.parentId && !b.sunk && bodyLeft(b) < BURNING;
      const sliding = !isVessel(b) && Math.abs(b.angle) > SLIDE_ANGLE;
      let st = this.boats.get(b.id);
      if (!st) {
        st = { sunk: b.sunk, sliding, burning: false };
        this.boats.set(b.id, st);
      } else {
        // A vessel that blew up splashes once the fireball has had its moment; a raft goes straight under.
        if (b.sunk && !st.sunk) this.sfx.sink(this.boomed && isVessel(b) ? 0.7 : 0);
        if (sliding && !st.sliding) this.sfx.creak();
        st.sunk = b.sunk;
        st.sliding = sliding;
      }
      if (burning !== st.burning) {
        st.burning = burning;
        if (burning) this.sfx.startFire(`fire:${b.id}`);
        else this.sfx.stopLoop(`fire:${b.id}`);
      }
    }
    for (const id of [...this.boats.keys()]) {
      if (seen.has(id)) continue;
      this.boats.delete(id);
      this.sfx.stopLoop(`fire:${id}`);
    }
  }

  /** The camera pulling out between turns gets a quiet breath of air. */
  private turns(view: ViewState): void {
    const phase = view.turn.phase;
    if (this.phase !== null && phase === 'between' && this.phase !== 'between') this.sfx.pullOut();
    this.phase = phase;
  }

  /** Coins leaving the purse in the shop ring; bounties arriving ring softly. */
  private purse(view: ViewState): void {
    const team = view.teams.find((t) => t.playerIds.includes(this.localId));
    if (!team) return;
    const c = team.coins;
    if (this.coins !== null && c !== this.coins) {
      if (c < this.coins && view.turn.phase === 'buy') this.sfx.coin();
      else if (c > this.coins && view.turn.phase !== 'buy') this.sfx.coin(true);
    }
    this.coins = c;
  }
}
